import { Component, Input, Output, EventEmitter } from '@angular/core';
import { GuestPushService, PushAskSource } from '../../services/guest-push.service';
import { AnalyticsService } from '../../services/analytics.service';
import { trigger, transition, style, animate, query, group } from '@angular/animations';

@Component({
    selector: 'app-push-prompt',
    templateUrl: './push-prompt.component.html',
    styleUrls: ['./push-prompt.component.scss'],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                group([
                    animate('250ms ease-out', style({ opacity: 1 })),
                    query('.push-prompt-modal', [
                        style({ opacity: 0, transform: 'scale(0.9)' }),
                        animate('300ms 50ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
                    ])
                ])
            ]),
            transition(':leave', [
                group([
                    query('.push-prompt-modal', [
                        animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' }))
                    ]),
                    animate('250ms 50ms ease-in', style({ opacity: 0 }))
                ])
            ])
        ])
    ],
    standalone: false
})
export class PushPromptComponent {
  @Input() isOpen = false;
  /**
   * The engagement that earned this ask. Reported on every event this prompt logs, so the funnel
   * still splits by what the diner had just been browsing — it no longer changes what the prompt
   * SAYS — except 'order', which asks a different question (see `isOrderAsk`). Null only as a
   * defensive default; nothing opens the prompt without one.
   */
  @Input() source: PushAskSource | null = null;
  @Output() accepted = new EventEmitter<void>();
  @Output() dismissed = new EventEmitter<void>();

  isLoading = false;

  /**
   * Asked from Order: "tell me when the venue accepts", not the menu's "don't miss out". It also
   * registers no scan, and its "Not now" is remembered — see GuestPushService's order-ask region.
   */
  get isOrderAsk(): boolean {
    return this.source === 'order';
  }

  constructor(
    private guestPushService: GuestPushService,
    private analytics: AnalyticsService
  ) {}

  async enableNotifications(): Promise<void> {
    // The tap on *our* prompt, logged before the browser dialog opens. This is the denominator the
    // permission events divide into: everything after it is the OS's decision, not the diner's
    // opinion of the prompt.
    this.analytics.log('push_prompt_enable_tapped', { prompt_source: this.source });
    this.isLoading = true;
    const success = this.isOrderAsk
      ? await this.guestPushService.requestPermissionForOrder()
      : await this.guestPushService.requestPermissionAndSubscribe();
    this.isLoading = false;
    if (success) {
      this.accepted.emit();
    } else {
      // A failure here already logged `push_permission_denied` (or fell over on the token); it is
      // not a dismissal, so the close path stays silent and only `dismiss()` counts one.
      this.dismissed.emit();
    }
  }

  dismiss(): void {
    // Carries the source like the impression and the tap do, so the whole funnel splits by which
    // copy the diner actually saw — otherwise the per-source acceptance rate is still unreadable.
    this.analytics.log('push_prompt_dismissed', { prompt_source: this.source });
    if (this.isOrderAsk) this.guestPushService.markOrderAskDeclined();
    // No budget bookkeeping here. The slot for the engagement this ask was attributed to was spent
    // when the prompt opened, and dismissing THIS ask must not spend the other source's — one
    // ignored Explore prompt is not a refusal of the Events-earned one.
    this.dismissed.emit();
  }
}
